import { useState, useEffect, useContext } from "react";
import { TENANT_PLATFORM } from "@hiver/bifrost-js";
import { getAppConfig } from "../../Bifrost/api";

import { AutherContext, ToastContext, KBContext } from "../index";
import useNavigateWithSearch from "../../Context/KBContext/hooks/useNavigateWithSearch";
import { open_popup, onOAuthSuccess, getGoogleAuthUrl } from "../../pages/Login/utils";
import { getUserDetails } from "../../apiRequest";
import { DASHBOARD_ALLOWED_ROLES, INTERNAL_KB_ALLOWED_ROLES } from "../../Constants/KBContext.constants";
import { getState, STORAGE_KEYS, clearLocalStoragePreserving } from "../../Constants/LocalStorage";
import {
    getUserDetailsviaV2ProfileMe,
    getStoredAccessTokenHOT,
    validateTokenHOT,
    getHeimdallCodeHOT,
} from "../../Service/LoginServices";
import { initConnectorsBifrost } from "../../Bifrost";
import { FTR_KB_LOGIN_FORCE_OAUTH } from "../../Constants";
import { isFeatureEnabled } from "../../utils";

export const NewAutherProvider = (props) => {
    const { setNoNet } = useContext(ToastContext);
    const { setHiddenState, setShowDashboardFooter, data, data: { isInternal = false }, availableFeatures } = useContext(KBContext);
    const kbLoginForceOAuth = isFeatureEnabled(FTR_KB_LOGIN_FORCE_OAUTH, availableFeatures);
    const [isProfileMeDone, setIsProfileMeDone] = useState(false);
    const [showError, setShowError] = useState(false);
    const [email, setEmail] = useState("");
    const [refetchToken, setRefetchToken] = useState(true);
    const [showLoader, setShowLoader] = useState(false);
    const [userSession, setUserSession] = useState(false);
    // flag used to redirect within KB, if false, KB Website redirects to Login Page
    const [redirectToken, setRedirectToken] = useState(true);
    // flag used to direct to OAuth Page, if true OAuth page is opened, else only token is fetched
    const [redirectPage, setRedirectPage] = useState(false);
    const [userDetails, setUserDetails] = useState(null);
    const [hasPermission, setHasPermission] = useState(false);
    const [loginInitiated, setLoginInitiated] = useState(false);
    const navigate = useNavigateWithSearch();

    const initiateLogin = ({email}) => {
        clearLocalStoragePreserving();
        return getGoogleAuthUrl(email, { forceLogin: kbLoginForceOAuth }).then((gAuth) => {
            gAuth && open_popup(
                gAuth,
                onOAuthSuccess,
                email,
                setRefetchToken,
                setRedirectPage,
                setEmail,
                setShowError,
                setLoginInitiated
            );
        }).catch(e => console.error('Error while getting google auth: ', e));
    }

    const getTokenFunction = () => {
        const handleFetchUserDetails = async () => {
            setHiddenState(true);
            try {
                const response = await getUserDetails();
                const userData = response?.data?.data;
                const userRole = userData?.userRole;
                const hasPermission = INTERNAL_KB_ALLOWED_ROLES.includes(userRole);
                console.debug({ hasPermission });
                if(!hasPermission){
                  throw response;
                }
                setUserDetails(userData);
                setHasPermission(hasPermission);
                setShowDashboardFooter(DASHBOARD_ALLOWED_ROLES.includes(userRole));
            } catch (error) {
                setUserDetails(null);
                setHasPermission(false);
                setShowDashboardFooter(false);
                throw error;
            }
        };
    
        const handleAuthSuccess = async (resp) => {
            setNoNet(false);
            const { user_id, usergroup_id, tenantPlatform, getTokenForLogin } = resp?.data ?? resp;
            const { appEnv, tier } = getAppConfig();
            initConnectorsBifrost( { 
              appEnv,
              tier,
              userId: user_id,
              userGroupId: usergroup_id,
              tenantPlatform,
              getTokenForLogin
            })
            if (isInternal) {
                try{
                  await handleFetchUserDetails();
                }
                catch{
                  navigate("/login");
                }
                setRedirectToken(true); // don't navigate to login
                setShowLoader(false);
            } else {
              setHasPermission(true);
              setShowDashboardFooter(true);
            }
            setLoginInitiated(true);
            setIsProfileMeDone(true);
        }
    
        const handleOAuth = () => {
            clearLocalStoragePreserving();
            getGoogleAuthUrl(email, { forceLogin: kbLoginForceOAuth })
                .then(gAuth => {
                    gAuth &&
                        open_popup(
                            gAuth,
                            onOAuthSuccess,
                            email,
                            setRefetchToken,
                            setRedirectPage,
                            setEmail,
                            setShowError,
                            setLoginInitiated
                        );
                    }
                )
                .catch(e => console.error("Error while getting Google auth:", e));
        };
    
        const handleError = async (rej) => {
            setNoNet(false);
            setRedirectToken(false); // redirects to login page
            setShowLoader(false);
            if (rej?.response?.status === 401) {
                if (isInternal) {
                    try {
                        if (redirectPage) {
                            handleOAuth();
                        }
                    } catch{
                        setRedirectToken(false); // redirects to login page
                    }
                } else {
                    setHasPermission(true);
                    setShowDashboardFooter(false);
                    // external kb doesnt need to have a dependency here. 
                    // so changing it to true to allow users to make api calls.
                    setIsProfileMeDone(true);
                }
            }
        };
        getUserDetailsviaV2ProfileMe()
            .then(handleAuthSuccess)
            .catch(handleError);
    };

    const initiateAuthWithUserDetails = ({ user_id, usergroup_id }) => {
        const handleFetchUserDetails = async () => {
            setHiddenState(true);
            try {
                const response = await getUserDetails();
                const userData = response?.data?.data;
                const userRole = userData?.userRole;
                const hasPermission = INTERNAL_KB_ALLOWED_ROLES.includes(userRole);
                if (!hasPermission) throw response;
                setUserDetails(userData);
                setHasPermission(hasPermission);
                setShowDashboardFooter(DASHBOARD_ALLOWED_ROLES.includes(userRole));
            } catch (error) {
                setUserDetails(null);
                setHasPermission(false);
                setShowDashboardFooter(false);
                if (isInternal) throw error;
            }
        };
        const { appEnv, tier } = getAppConfig();
        initConnectorsBifrost({
            appEnv,
            tier,
            userId: user_id,
            userGroupId: usergroup_id,
            tenantPlatform: TENANT_PLATFORM.HIVERWEB,
            getTokenForLogin: getHeimdallCodeHOT,
        });
        setNoNet(false);
        if (isInternal) {
            return handleFetchUserDetails()
                .then(() => {
                    setRedirectToken(true);
                    setShowLoader(false);
                    setLoginInitiated(true);
                    setIsProfileMeDone(true);
                })
                .catch(() => { navigate("/login"); });
        }
        setHasPermission(true);
        setShowDashboardFooter(true);
        setRedirectToken(true);
        setLoginInitiated(true);
        setIsProfileMeDone(true);
        return Promise.resolve();
    };

    const tryRestoreSessionFromStoredToken = async () => {
        const activeTenant = getState(STORAGE_KEYS.KB_ACTIVE_TENANT);
        if (activeTenant === TENANT_PLATFORM.GMAIL) return false;
        const accessToken = getStoredAccessTokenHOT();
        if (!accessToken) return false;
        try {
            const res = await validateTokenHOT(accessToken);
            const payloadData = res?.data?.data;
            if (payloadData?.id == null || payloadData?.accounts?.[0]?.id == null) return false;
            await initiateAuthWithUserDetails({
                user_id: payloadData.id,
                usergroup_id: payloadData.accounts[0].id,
            });
            return true;
        } catch {
            return false;
        }
    };

    useEffect(() => {
        if (refetchToken && data?.knowledgeBaseId) {
            tryRestoreSessionFromStoredToken().then((restored) => {
                if (!restored) getTokenFunction();
                setRefetchToken(false);
            });
        }
    // disabling eslint to hide dependecy warning concerning getTokenFunction,
    // reason being that need to wrap it in a useCalback, which will result in the
    // function being reconstructed and cached multiple times as it will have a lot 
    // of dependecies itself, don't want to rework too much as it is legacy code    
    // please be mindful of dependencies while extending the code
    // as linter warnings won't be shown for missing dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refetchToken, data, setRefetchToken]);

    return (
        <AutherContext.Provider
            value={{
                setEmail: setEmail,
                email: email,
                isProfileMeDone,
                redirectToken: redirectToken,
                setShowError,
                showError,
                setRedirectToken: setRedirectToken,
                userSession,
                setUserSession,
                refetchToken,
                setRefetchToken,
                initiateLogin,
                setShowLoader,
                setRedirectPage,
                showLoader,
                userDetails,
                hasPermission,
                loginInitiated,
                setLoginInitiated,
                initiateAuthWithUserDetails,
            }}
        >
            {props.children}
        </AutherContext.Provider>
    );
}
